// Anforderungs-Matrix, Diagramm-Ansicht ("Modern"): EINE Achse. X = wie viele Personen im Team
// das Kriterium tatsaechlich TRAGEN (met), auf einer Skala 0..teamSize. Y ist rein binaer -
// erfuellt (inkl. kulant, oben) vs. nicht erfuellt (unten) - noch keine weitere Ebene, das war
// gegenueber einem echten X/Y-Koordinatensystem (Personenzahl vs. Fachkompetenz-Stufe) der einfachere,
// direkter lesbare Schritt. Mehrere Kriterien mit aehnlichem met-Wert wuerden sich sonst
// ueberlappen, deshalb weichen sie in eigene "Lanes" innerhalb ihrer Haelfte aus (assignLanes).
// Legende oben rechts filtert per Klick (bei vielen Kriterien nur einen Status sehen). Klick auf
// eine Bubble klappt auf, wer sie traegt.
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIntl, type IntlShape } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import type { SkillLevel } from "@/lib/skill-levels";
import { LockClosedIcon } from "../lock-icons";
import type { RequirementAnalysis, ResultPerson } from "../match-result-derive";
import type { RequirementMatrixDiagramProps } from "./types";

// Ohne das ist "25/26" allein nicht einzuordnen: nah an der Erfuellt-Schwelle kann trotzdem
// "nicht erfuellt" heissen, wenn die Regel ALL ist (jede Person muss es tragen). Macht sichtbar,
// WELCHE Schwelle gemeint ist, statt dass man sie aus dem Kontext erraten muss.
function ruleLabel(intl: IntlShape, a: RequirementAnalysis): string {
  switch (a.operator) {
    case "ALL":
      return intl.formatMessage({ id: "match.result.requirementDiagram.ruleAll", defaultMessage: "alle" });
    case "ANY":
      return intl.formatMessage({ id: "match.result.requirementDiagram.ruleAny", defaultMessage: "mind. 1" });
    case "MIN":
      return a.required != null
        ? intl.formatMessage(
            { id: "match.result.requirementDiagram.ruleMin", defaultMessage: "mind. {n}" },
            { n: a.required },
          )
        : intl.formatMessage({ id: "match.result.requirementDiagram.ruleMinUnknown", defaultMessage: "mind. ?" });
    case "MAX":
      return a.required != null
        ? intl.formatMessage(
            { id: "match.result.requirementDiagram.ruleMax", defaultMessage: "höchstens {n}" },
            { n: a.required },
          )
        : intl.formatMessage({
            id: "match.result.requirementDiagram.ruleMaxUnknown",
            defaultMessage: "höchstens ?",
          });
    case "EXACT":
      return a.required != null
        ? intl.formatMessage(
            { id: "match.result.requirementDiagram.ruleExact", defaultMessage: "genau {n}" },
            { n: a.required },
          )
        : intl.formatMessage({
            id: "match.result.requirementDiagram.ruleExactUnknown",
            defaultMessage: "genau ?",
          });
  }
}

// Ampel-Status eines Kriteriums - treibt die Dot-Farbe, die Legende UND (fulfilled/lenient vs.
// unfulfilled) die Y-Haelfte. Eine Quelle fuer "was bedeutet welche Farbe".
type CriterionStatus = "fulfilled" | "lenient" | "unfulfilled";

function criterionStatus(a: RequirementAnalysis, lenient: boolean): CriterionStatus {
  if (!a.fulfilled) return "unfulfilled";
  return lenient ? "lenient" : "fulfilled";
}

// Kulant erfuellt zaehlt zur oberen (erfuellten) Haelfte - eigene Ampelfarbe, aber dieselbe
// Y-Seite; nur "unfulfilled" sitzt unten.
function isFulfilledSide(status: CriterionStatus): boolean {
  return status !== "unfulfilled";
}

// Ampelfarbe des Dots (erfuellt/kulant/nicht erfuellt).
const STATUS_DOT_CLASS: Record<CriterionStatus, string> = {
  fulfilled: "bg-emerald-500",
  lenient: "bg-amber-500",
  unfulfilled: "bg-rose-500",
};

const LEGEND_ITEMS: Array<{ status: CriterionStatus; id: string; defaultMessage: string }> = [
  { status: "fulfilled", id: "match.result.ranking.tabNameFulfilled", defaultMessage: "Erfüllt" },
  { status: "lenient", id: "match.result.ranking.tabNameLenient", defaultMessage: "Kulant erfüllt" },
  { status: "unfulfilled", id: "match.result.ranking.tabNameUnfulfilled", defaultMessage: "Nicht erfüllt" },
];

// Personenzahl-Ticks der X-Achse: 0/25/50/75/100 % der Teamgroesse, gerundet auf ganze Personen.
const PERSON_TICKS = [0, 0.25, 0.5, 0.75, 1];

// Hoehe einer Lane in Pixeln - Platz fuer eine Bubble-Reihe plus etwas Luft, damit sich
// benachbarte Lanes nicht beruehren.
const LANE_HEIGHT_PX = 40;

// Zusaetzlicher Puffer am AEUSSEREN Rand jeder Haelfte (weiteste Lane von der X-Achse entfernt) -
// addiert auf die Gesamthoehe, OHNE die Lane-Offsets selbst zu aendern. bottom/top der Bubbles
// bleibt relativ zur X-Achse gleich, es entsteht nur mehr Leerraum am oberen bzw. unteren Rand des
// Diagramms, statt dass die aeusserste Bubble direkt an der Box-Kante klebt.
const EDGE_PADDING_PX = 20;

// Mindestabstand in PIXELN zwischen zwei Bubble-Kanten auf derselben Lane. Ersetzt einen frueheren
// festen Prozentwert (14% der Diagrammbreite, grob an typischen Chip-Breiten kalibriert), der bei
// langen Labels ("Datenschutzbeauftragter: mind. 3") zu eng war - die geschaetzte Breite lag unter
// der tatsaechlich gerenderten, Chips ueberlappten sichtbar (spark #250 Review). assignLanes
// rechnet jetzt mit der ECHTEN, gemessenen Chip-Breite statt einer Schaetzung, siehe useLaneWidths.
const LANE_MIN_GAP_PX = 12;

// Abstand zum Soll, in Personen. Positiv = Puffer, negativ = Luecke. Nicht ueber Kriterien
// hinweg vergleichbar (zwei Kriterien mit verschiedenen Soll-Zahlen sagen nichts uebereinander)
// und deshalb ausdruecklich KEINE Skala - nur die Reihenfolge innerhalb eines Stapels.
// Der Operator entscheidet die Richtung: bei MAX ist WENIGER besser, bei EXACT zaehlt der
// Betrag der Abweichung.
function targetMargin(a: RequirementAnalysis, teamSize: number): number {
  const target = a.required ?? teamSize;
  switch (a.operator) {
    case "MAX":
      return target - a.met;
    case "EXACT":
      return -Math.abs(a.met - target);
    default:
      return a.met - target;
  }
}

// Greedy Lane-Zuweisung: jedes Element bekommt die niedrigste Lane, in der sein [links,rechts]-Rand
// (aus xPercent + der gemessenen Chip-Breite in Pixeln) keinen bereits platzierten Chip auf dieser
// Lane um weniger als LANE_MIN_GAP_PX unterschreitet. Reihenfolge = Eingabereihenfolge
// Filtern/Aendern anderer Kriterien scheinbar grundlos - eine Sortierung nach X waere etwas
// anderes gewesen und ist bewusst nicht passiert. Die Eingabereihenfolge entscheidet trotzdem
// etwas: WER innerhalb eines erzwungenen Stapels innen an der Achse liegt. Der Aufrufer sortiert
// dafuer nach Abstand zum Soll (byMargin); wer mit niemandem kollidiert, landet ohnehin auf
// Lane 0. Ohne Breite (noch nicht gemessen,
// containerWidthPx===0) faellt ein Item ohne Kollisionspruefung auf Lane 0 - betrifft nur den
// allerersten Render-Frame vor dem Messen-Effekt, siehe useLaneWidths.
function assignLanes<T>(
  items: T[],
  xPercentOf: (item: T) => number,
  widthPxOf: (item: T) => number,
  containerWidthPx: number,
): T[][] {
  // Vor dem Messen ist jede Breite 0, also saessen ALLE Chips bei Pixel 0 und kollidierten
  // miteinander - eine Lane pro Kriterium, ein Frame lang maximal hoch, dann klappt es zusammen.
  // Eine Lane ohne Pruefung ist der stabilere Zwischenzustand (und genau das, was der Kommentar
  // oben zusagt).
  if (containerWidthPx <= 0) return items.length > 0 ? [items] : [];
  const lanes: Array<Array<{ item: T; leftPx: number; rightPx: number }>> = [];
  for (const item of items) {
    const centerPx = (xPercentOf(item) / 100) * containerWidthPx;
    const halfWidthPx = widthPxOf(item) / 2;
    const leftPx = centerPx - halfWidthPx;
    const rightPx = centerPx + halfWidthPx;
    let laneIndex = 0;
    while (
      lanes[laneIndex]?.some(
        (placed) => leftPx < placed.rightPx + LANE_MIN_GAP_PX && rightPx > placed.leftPx - LANE_MIN_GAP_PX,
      )
    )
      laneIndex++;
    (lanes[laneIndex] ??= []).push({ item, leftPx, rightPx });
  }
  return lanes.map((lane) => lane.map((placed) => placed.item));
}

// Misst die Diagrammbreite (ResizeObserver - aendert sich mit Card-/Fensterbreite) und die
// tatsaechlich gerenderte Breite jeder Bubble ueber einen Callback-Ref pro Kriterien-Index. Inhalt
// einer Bubble aendert sich nur bei neuen Daten, nicht bei Resize, daher genuegt einmaliges Messen
// je Index; verwaiste Eintraege (Kriterium durch Legenden-Filter ausgeblendet) werden entfernt,
// sonst wuerden unsichtbare Chips ihre alte Breite auf ewig mitschleppen.
function useLaneWidths(visibleKeys: number[]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidthPx, setContainerWidthPx] = useState(0);
  const widthsRef = useRef<Map<number, number>>(new Map());
  const [, bumpVersion] = useState(0);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidthPx(el.offsetWidth);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width != null) setContainerWidthPx(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const keep = new Set(visibleKeys);
    for (const key of widthsRef.current.keys()) {
      if (!keep.has(key)) widthsRef.current.delete(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- visibleKeys ist ein frisches Array pro Render, Inhalt zaehlt
  }, [visibleKeys.join(",")]);

  const measureRef = (key: number) => (el: HTMLButtonElement | null) => {
    if (!el) return;
    const w = el.offsetWidth;
    if (widthsRef.current.get(key) !== w) {
      widthsRef.current.set(key, w);
      bumpVersion((v) => v + 1);
    }
  };

  return { containerRef, containerWidthPx, measureRef, widthPxOf: (key: number) => widthsRef.current.get(key) ?? 0 };
}

// Zweite Zeile im Popover-Chip: Kulanz-Hinweis und/oder Stufe dieser Person, "·"-getrennt wie an
// anderen Stellen im Ergebnis (z.B. ruleLabel-Chip). level ist null bei Nicht-Expertise-Kriterien
// oder unbewerteten Personen - dann faellt die Stufen-Angabe einfach weg statt eine Luecke zu zeigen.
function contributorSub(intl: IntlShape, level: SkillLevel | null, closeLabel?: string): string | undefined {
  const parts = [
    closeLabel,
    level
      ? intl.formatMessage(
          { id: "match.result.requirementDiagram.personLevel", defaultMessage: "Stufe: {level}" },
          { level: intl.formatMessage({ id: level.nameId, defaultMessage: level.name }) },
        )
      : null,
  ].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

// Ein Popover-Eintrag: die Person (PersonResultRow) plus - falls vorhanden - der KI-Begruendungstext
// darunter, den genesis fuer dieses Kriterium/diese Person schon mitliefert (derselbe Rohwert, den
// FactsAndSkillsBody an anderer Stelle bereits zeigt). Eigene kleine Komponente statt Duplizieren
// zwischen metPersons/closePersons-Map, die sich nur im closeLabel unterscheiden.
function ContributorEntry({
  p,
  level,
  aiReason,
  closeLabel,
}: {
  p: ResultPerson;
  level: SkillLevel | null;
  aiReason: string | null;
  closeLabel?: string;
}) {
  const intl = useIntl();
  const reason = aiReason?.trim();
  return (
    <PersonResultRow
      person={p}
      title={p.name}
      sub={contributorSub(intl, level, closeLabel)}
      description={reason || undefined}
      embedded
    />
  );
}

function ContributorPopover({
  a,
  personById,
  onClose,
  position,
}: {
  a: RequirementAnalysis;
  personById: Map<string, ResultPerson>;
  onClose: () => void;
  position: { top: number; left: number };
}) {
  const intl = useIntl();
  const metPersons = a.metPersonIds.map((id) => personById.get(id)).filter((p): p is ResultPerson => Boolean(p));
  const closePersons = a.closePersonIds
    .map((id) => personById.get(id))
    .filter((p): p is ResultPerson => Boolean(p));
  const closeLabel = intl.formatMessage({ id: "match.result.requirementDiagram.closeLabel", defaultMessage: "Knapp" });
  // Per Portal an document.body statt als Kind/Geschwister im Bubble-Wrapper: das Popover hat
  // dann keinerlei DOM-Beziehung mehr zum Button, sein Erscheinen/Verschwinden kann also nie
  // dessen Stacking-Context/Compositing-Layer beeinflussen. Vorher (Geschwister im selben
  // position:absolute-Wrapper) reichte schon ein neues Kind mit z-index + Scroll, damit Chromium
  // den Button-Text kurz auf einer frisch erzeugten Layer ohne Subpixel-Antialiasing rendert -
  // sichtbar als kurzes Unscharfwerden beim Aufklappen (reproduzierbar bei scrollenden Popovern
  // mit vielen Personen, nicht bei kurzen Listen).
  return createPortal(
    <>
      <span
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="fixed inset-0 z-20 cursor-default"
      />
      {/* PersonResultRow (embedded) statt einer eigenen Chip-Komponente - dasselbe Primitive, das
          Profile/Kontaktlisten schon nutzen, statt hier eine eigene Optik zu erfinden. */}
      {/* rounded-2xl statt der box-flat-Standardrundung (rounded-md, ueberschrieben da Utilities
          nach @layer components kommen): der ovale Chip und ein eckiger Popover-Rahmen wirkten
          nicht zusammengehoerig. */}
      <div
        className="bg-page box-flat absolute z-30 max-h-80 w-96 -translate-x-1/2 overflow-y-auto rounded-2xl p-2 text-left shadow-lg"
        style={{ top: position.top, left: position.left }}
      >
        <div className="space-y-1">
          {metPersons.map((p) => (
            <ContributorEntry
              key={p.id}
              p={p}
              level={a.personDetails[p.id]?.level ?? null}
              aiReason={a.personDetails[p.id]?.aiReason ?? null}
            />
          ))}
          {closePersons.map((p) => (
            <ContributorEntry
              key={p.id}
              p={p}
              level={a.personDetails[p.id]?.level ?? null}
              aiReason={a.personDetails[p.id]?.aiReason ?? null}
              closeLabel={closeLabel}
            />
          ))}
          {metPersons.length === 0 && closePersons.length === 0 && (
            <span className="text-smycles-label text-ink-muted">
              {intl.formatMessage({
                id: "match.result.requirementDiagram.noContributors",
                defaultMessage: "Niemand im Team trägt das bei.",
              })}
            </span>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function RequirementBubble({
  a,
  label,
  personById,
  xPercent,
  offsetPx,
  side,
  open,
  onToggle,
  lenient,
  measureRef,
}: {
  a: RequirementAnalysis;
  label: string;
  personById: Map<string, ResultPerson>;
  xPercent: number;
  // Abstand zur X-Achse in Pixeln, Mittelpunkt der zugewiesenen Lane.
  offsetPx: number;
  // "fulfilled" waechst von der Achse nach OBEN (bottom: offsetPx), "unfulfilled" nach UNTEN
  // (top: offsetPx) - beide Haelften bauen von der Achse aus nach aussen auf.
  side: "fulfilled" | "unfulfilled";
  open: boolean;
  onToggle: () => void;
  lenient: boolean;
  // Misst die gerenderte Chip-Breite fuer die Kollisionspruefung in assignLanes (useLaneWidths).
  measureRef: (el: HTMLButtonElement | null) => void;
}) {
  const intl = useIntl();
  // Card bleibt neutral - erfuellt/kulant/nicht-erfuellt traegt nur der kleine Dot (analog zum
  // Punkt in FactsAndSkillsBody). Kulant ist eine EIGENE Ampelfarbe, keine Abstufung von gruen -
  // genau die Bruchstelle, die leniencyDependentCriteria dokumentiert: "erfuellt" und "kulant
  // erfuellt" duerfen sich nicht gleich lesen (spark #236).
  const dotClasses = STATUS_DOT_CLASS[criterionStatus(a, lenient)];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  // Position einmal beim Oeffnen messen, nicht laufend an Scroll gebunden - ein Scroll-/Resize-
  // Listener oder floating-ui waere noetig, damit das Popover auch waehrend des Scrollens sitzen
  // bleibt; fuer dieses Diagramm (kurze Interaktion, kein langes Scrollen waehrend offen) reicht
  // die einmalige Messung.
  useLayoutEffect(() => {
    if (!open) {
      setPopoverPosition(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPopoverPosition({
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
  }, [open]);
  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${xPercent}%`, [side === "fulfilled" ? "bottom" : "top"]: `${offsetPx}px` }}
    >
      <button
        ref={(el) => {
          buttonRef.current = el;
          measureRef(el);
        }}
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="bg-ink/[0.05] text-ink ring-1 ring-ink/10 hover:bg-ink/[0.08] relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-smycles-meta font-medium whitespace-nowrap outline-none transition focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
      >
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClasses}`} aria-hidden="true" />
        {a.isMust && (
          <span className="shrink-0 opacity-60" aria-hidden="true">
            <LockClosedIcon />
          </span>
        )}
        {label}
        {lenient && (
          <span className="text-smycles-micro font-medium text-amber-500">
            {intl.formatMessage({ id: "match.result.facts.lenientMark", defaultMessage: "Kulant" })}
          </span>
        )}
        <span className="text-smycles-micro tabular-nums opacity-60">
          {a.met}/{a.teamSize} · {ruleLabel(intl, a)}
        </span>
      </button>
      {/* Geschwister-Element statt Kind des Buttons - aus zwei Gruenden. (1) PersonResultRow
          rendert bei Bedarf einen <Link> (<a>), und ein <a> verschachtelt in einem <button> ist
          ungueltiges HTML mit inkonsistentem Klick-/Tastaturverhalten. (2) Bei vielen Personen
          wird das Popover wirklich scrollbar, und Chromium hebt echt scrollende Container oft auf
          eine eigene Compositing-Layer - als Kind des Buttons riss das den Button-Text mit auf die
          neue Layer und liess ihn kurz unscharf werden. Seit dem Portal-Umbau (s.u.) ist das
          Popover ohnehin kein Kind/Geschwister im DOM-Sinn mehr. */}
      {open && popoverPosition && (
        <ContributorPopover a={a} personById={personById} onClose={onToggle} position={popoverPosition} />
      )}
    </div>
  );
}

export function RequirementMatrixDiagram({ analysis, persons, label, lenientIndexes }: RequirementMatrixDiagramProps) {
  const intl = useIntl();
  const personById = new Map(persons.map((p) => [p.id, p]));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // Legende als Sichtbarkeits-Filter (Default: alle drei Status sichtbar, Klick = ausblenden) -
  // hiddenStatuses statt visibleStatuses, damit ein leeres Set (Anfangszustand) "nichts
  // ausgeblendet" statt "nichts sichtbar" bedeutet.
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<CriterionStatus>>(() => new Set());
  const toggleStatus = (status: CriterionStatus) => {
    setHiddenStatuses((cur) => {
      const next = new Set(cur);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
    // Ein ausgeblendetes Kriterium kann sein Popover nicht mehr schliessen - ohne Reset bliebe es
    // (unsichtbar) offen haengen.
    setOpenIndex(null);
  };
  const visibleAnalysis = analysis.filter(
    (a) => !hiddenStatuses.has(criterionStatus(a, lenientIndexes.has(a.index))),
  );

  // Rand-Padding, damit Bubbles am Rand nicht abgeschnitten werden - dieselbe Skalierung fuer
  // Bubbles UND Achsen-Ticks, ein Tick sitzt so immer exakt auf seinem Wert.
  const teamSize = analysis[0]?.teamSize ?? 0;
  const ratioToPercent = (ratio: number) => 12 + ratio * 76;
  const xPercent = (a: RequirementAnalysis) => (teamSize > 0 ? ratioToPercent(a.met / teamSize) : 50);

  const fulfilledItems = visibleAnalysis.filter((a) =>
    isFulfilledSide(criterionStatus(a, lenientIndexes.has(a.index))),
  );
  const unfulfilledItems = visibleAnalysis.filter(
    (a) => !isFulfilledSide(criterionStatus(a, lenientIndexes.has(a.index))),
  );
  // Innen an der Achse das Knappste, aussen das Deutlichste - in BEIDEN Haelften, weil die
  // Achse das Soll ist: Abstand zur Achse liest sich dann als Abstand zum Soll. Der Betrag,
  // damit die Regel unter der Achse dieselbe ist (grosse Luecke = weit unten). Gleicher
  // Abstand -> Kriterien-Index, damit die Zuordnung stabil bleibt.
  const byMargin = (items: RequirementAnalysis[]) =>
    [...items].sort(
      (x, y) =>
        Math.abs(targetMargin(x, teamSize)) - Math.abs(targetMargin(y, teamSize)) ||
        x.index - y.index,
    );
  const { containerRef, containerWidthPx, measureRef, widthPxOf } = useLaneWidths(
    visibleAnalysis.map((a) => a.index),
  );
  const widthPxOfItem = (a: RequirementAnalysis) => widthPxOf(a.index);
  // Sortierung und Kollisionspruefung greifen ineinander, ohne sich zu widersprechen: die
  // gemessene Breite entscheidet, OB gestapelt werden muss, der Abstand zum Soll, WER dann innen
  // liegt.
  const fulfilledLanes = assignLanes(byMargin(fulfilledItems), xPercent, widthPxOfItem, containerWidthPx);
  const unfulfilledLanes = assignLanes(byMargin(unfulfilledItems), xPercent, widthPxOfItem, containerWidthPx);
  // Mindestens eine Lane Hoehe behalten, auch wenn eine Haelfte (gerade) leer ist - sonst
  // kollabiert die Haelfte komplett und "ERFÜLLT ↑"/"NICHT ERFÜLLT ↓" haben keinen Platz mehr.
  const fulfilledHeight = Math.max(1, fulfilledLanes.length) * LANE_HEIGHT_PX + EDGE_PADDING_PX;
  const unfulfilledHeight = Math.max(1, unfulfilledLanes.length) * LANE_HEIGHT_PX + EDGE_PADDING_PX;

  return (
    <div data-testid="requirement-matrix-diagram">
      {/* Legende als Filter: Klick blendet den Status aus (De-Select), Default alle sichtbar -
          nuetzlich bei vielen Kriterien, um sich z.B. nur auf "Nicht erfuellt" zu konzentrieren. */}
      <div className="mb-3 flex items-center justify-end gap-4 text-smycles-micro tracking-[0.05em] text-ink-muted">
        {LEGEND_ITEMS.map(({ status, id, defaultMessage }) => {
          const visible = !hiddenStatuses.has(status);
          return (
            <button
              key={status}
              type="button"
              aria-pressed={visible}
              onClick={() => toggleStatus(status)}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 outline-none transition focus-visible:ring-2 focus-visible:ring-ink-accent-ui ${visible ? "ring-1 ring-ink/15" : "opacity-40"}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT_CLASS[status]}`} aria-hidden="true" />
              {intl.formatMessage({ id, defaultMessage })}
            </button>
          );
        })}
      </div>
      <div className="flex">
        {/* Y-Label-Spalte: rein binaer (erfuellt/nicht erfuellt), keine Skala/Ticks - "erstmal
            keine weitere Ebene" auf Y, das Zahlenmaterial (met/teamSize, Regel) steht bereits im
            Chip-Text. */}
        <div
          className="relative w-20 shrink-0"
          style={{ height: `${fulfilledHeight + unfulfilledHeight}px` }}
        >
          <span className="absolute top-0 left-0 text-smycles-micro font-medium tracking-[0.05em] text-emerald-600 uppercase">
            {intl.formatMessage({ id: "match.result.requirementDiagram.axisFulfilled", defaultMessage: "Erfüllt ↑" })}
          </span>
          <span className="absolute bottom-0 left-0 text-smycles-micro font-medium tracking-[0.05em] text-rose-600 uppercase">
            {intl.formatMessage({
              id: "match.result.requirementDiagram.axisUnfulfilled",
              defaultMessage: "Nicht erfüllt ↓",
            })}
          </span>
        </div>
        <div ref={containerRef} className="mx-4 flex-1">
          <div className="relative" style={{ height: `${fulfilledHeight}px` }}>
            {fulfilledLanes.map((lane, laneIndex) =>
              lane.map((a) => (
                <RequirementBubble
                  key={a.index}
                  a={a}
                  label={label(a)}
                  personById={personById}
                  xPercent={xPercent(a)}
                  offsetPx={(laneIndex + 0.5) * LANE_HEIGHT_PX}
                  side="fulfilled"
                  open={openIndex === a.index}
                  onToggle={() => setOpenIndex((cur) => (cur === a.index ? null : a.index))}
                  lenient={lenientIndexes.has(a.index)}
                  measureRef={measureRef(a.index)}
                />
              )),
            )}
          </div>
          {/* X-Achse: Personenzahl 0..teamSize, mittig zwischen den beiden Haelften. */}
          <div className="relative h-5">
            <div className="absolute inset-x-0 top-0 h-px bg-ink/15" />
            {PERSON_TICKS.map((t) => (
              <span
                key={t}
                className="absolute top-1 -translate-x-1/2 text-smycles-micro tabular-nums text-ink-muted"
                style={{ left: `${ratioToPercent(t)}%` }}
              >
                {Math.round(t * teamSize)}
              </span>
            ))}
          </div>
          <div className="relative" style={{ height: `${unfulfilledHeight}px` }}>
            {unfulfilledLanes.map((lane, laneIndex) =>
              lane.map((a) => (
                <RequirementBubble
                  key={a.index}
                  a={a}
                  label={label(a)}
                  personById={personById}
                  xPercent={xPercent(a)}
                  offsetPx={(laneIndex + 0.5) * LANE_HEIGHT_PX}
                  side="unfulfilled"
                  open={openIndex === a.index}
                  onToggle={() => setOpenIndex((cur) => (cur === a.index ? null : a.index))}
                  lenient={lenientIndexes.has(a.index)}
                  measureRef={measureRef(a.index)}
                />
              )),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
